import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import React from 'react';

import type { RevisionPage } from '@gitbook/hive-core';
import type { EditorContainer } from '@gitbook/slate-react';
import { type IconDefinition, MOBILE_BREAKPOINT } from '@gitbook/spine-react';

import type { AgentDockMode } from './layout';

/**
 * A conversation open in the dock.
 *
 * Two kinds coexist. `organization` conversations belong to the organization agent and are the
 * only ones the user can start. `change_request` conversations belong to the older change-request
 * agent: they are still produced by background work (findings, reviews, comment threads) and can be
 * opened from the history, but never started by hand and never opened automatically.
 */
export type AgentDockTab =
    | {
          kind: 'organization';
          conversationId: string;
      }
    | {
          kind: 'change_request';
          spaceId: string;
          changeRequestId: string;
          /** Omitted for the change-request agent's implicit default conversation. */
          conversationId?: string;
      }
    | {
          /** A background agent's transcript, which can be read but never continued. */
          kind: 'organization_background';
          conversationId: string;
          /** The conversation that started it, named in the header so there is a way back. */
          parentConversationId: string;
          /** A few words naming what it is doing, which is what the tab shows. */
          title: string;
      };

/**
 * The dock's persisted state, per organization.
 *
 * Tabs survive a reload: the messages live in the durable object, so reopening a tab costs a
 * reconnection and nothing else. `minimized` hides the window while keeping every tab open, which
 * is what the minimize button does — closing a tab is the only thing that drops a conversation
 * from the dock.
 */
type AgentDockState = {
    tabs: AgentDockTab[];
    activeTabId: string | null;
    /**
     * Conversation opened from the Agent button that nobody has written in yet.
     *
     * It has no tab: the Agent button is its tab. A conversation only earns a place in the bar once
     * it has been started, so opening the agent to look at it does not litter the row.
     */
    draftConversationId: string | null;
    minimized: boolean;
};

/**
 * Active-tab pointer for the draft conversation, which has no tab of its own.
 */
export const AGENT_DOCK_DRAFT_TAB_ID = 'draft';

/**
 * We preserve the latest conversation on the site agent screen for a limited time,
 * allowing the user to return to it if they come back under this time window.
 */
const AGENT_SCREEN_LATEST_CONVERSATION_EXPIRATION_MS = 2 * 60 * 60 * 1000;

/**
 * Latest conversation on the site agent screen by site id.
 */
const latestAgentScreenConversationAtom = atomWithStorage<
    Record<string, { conversationId: string; seenAt: number }>
>('agentScreenConversations', {}, undefined, { getOnInit: true });

/**
 * The conversation to reopen on a site's full-screen agent, if the user was on it recently, and a
 * way to record that they are on one now.
 */
export function useLatestAgentScreenConversation(
    siteId: string
): [string | undefined, (conversationId: string) => void] {
    const [conversations, setConversations] = useAtom(latestAgentScreenConversationAtom);
    const entry = conversations[siteId];
    const latestConversationId =
        typeof entry?.conversationId === 'string' && typeof entry.seenAt === 'number'
            ? entry.conversationId
            : undefined;
    const expiresAt =
        latestConversationId !== undefined
            ? entry.seenAt + AGENT_SCREEN_LATEST_CONVERSATION_EXPIRATION_MS
            : undefined;
    const [now] = React.useState(Date.now);

    const setLatestConversation = React.useCallback(
        (conversationId: string) =>
            setConversations((previous) => ({
                ...previous,
                [siteId]: { conversationId, seenAt: Date.now() },
            })),
        [setConversations, siteId]
    );

    return [
        expiresAt !== undefined && now < expiresAt ? latestConversationId : undefined,
        setLatestConversation,
    ];
}

/**
 * What a surface of the dock has to show.
 */
export type AgentDockSurface =
    | { kind: 'draft'; conversationId: string }
    | { kind: 'tab'; tab: AgentDockTab }
    | { kind: 'none' };

/**
 * The conversation a surface shows, or nothing.
 *
 * The dock's window can be put away, which is what `minimized` means. The anchored panel cannot —
 * it is on screen either way — so it goes on showing the active conversation, and a window
 * minimized in another window of the same browser says nothing about it.
 */
export function getAgentDockSurface(input: {
    tabs: AgentDockTab[];
    activeTabId: string | null;
    draftConversationId: string | null;
    minimized: boolean;
    mode: AgentDockMode;
}): AgentDockSurface {
    const { tabs, activeTabId, draftConversationId, minimized, mode } = input;

    if (mode === 'dock' && minimized) {
        return { kind: 'none' };
    }

    if (activeTabId === AGENT_DOCK_DRAFT_TAB_ID && draftConversationId) {
        return { kind: 'draft', conversationId: draftConversationId };
    }

    const tab = tabs.find((candidate) => getAgentDockTabId(candidate) === activeTabId);
    return tab ? { kind: 'tab', tab } : { kind: 'none' };
}

const EMPTY_STATE: AgentDockState = {
    tabs: [],
    activeTabId: null,
    draftConversationId: null,
    minimized: false,
};

const baseAgentDockStorage = createJSONStorage<Record<string, AgentDockState>>();

/**
 * The dock's persisted state, read through what this build understands.
 *
 * A tab in a shape this build does not know used to flow straight into the bar, throw on first
 * render, and take the whole dock down behind its error boundary with nothing in the console —
 * recoverable only by clearing storage. So the read repairs instead: an older shape is upgraded
 * where it can be, anything unreadable is dropped, and the result is written back.
 */
const agentDockStorage: typeof baseAgentDockStorage = {
    ...baseAgentDockStorage,
    getItem: (key, initialValue) => {
        let stored: unknown;
        try {
            stored = baseAgentDockStorage.getItem(key, initialValue);
        } catch {
            // Blocked by the browser, say. The dock starts empty rather than not at all.
            return {};
        }

        const { value: sanitized, repaired } = readAgentDockStorage(stored);

        if (!repaired) {
            // Handed back as it is, keeping the identity the base storage memoized: a copy would
            // re-render every consumer for a read that changed nothing.
            return minimizeOnPhone(stored as Record<string, AgentDockState>);
        }

        // Written back so the corruption does not outlive the load that found it. A courtesy,
        // unlike reading clean state — so a failure to write is not worth surfacing.
        try {
            baseAgentDockStorage.setItem(key, sanitized);
        } catch {}

        return minimizeOnPhone(sanitized);
    },
    // Another browser tab writing storage reaches this one through here.
    subscribe: baseAgentDockStorage.subscribe
        ? (key, callback, initialValue) =>
              baseAgentDockStorage.subscribe!(
                  key,
                  (value) => callback(sanitizeAgentDockStorage(value)),
                  initialValue
              )
        : undefined,
};

/**
 * Dock state keyed by organization, so switching organizations doesn't carry another one's
 * conversations along.
 */
const agentDockStateAtom = atomWithStorage<Record<string, AgentDockState>>(
    'agentDock',
    {},
    agentDockStorage,
    { getOnInit: true }
);

/**
 * The persisted dock state as this build can use it, and never a throw: persisted state is input,
 * not memory, so anything unrecognizable reduces to an empty record.
 */
export function sanitizeAgentDockStorage(value: unknown): Record<string, AgentDockState> {
    return readAgentDockStorage(value).value;
}

/** The same read, saying whether anything had to be repaired, so the caller can write it back. */
function readAgentDockStorage(value: unknown): {
    value: Record<string, AgentDockState>;
    repaired: boolean;
} {
    if (!isRecord(value)) {
        return { value: {}, repaired: value !== undefined && value !== null };
    }

    const sanitized: Record<string, AgentDockState> = {};
    let repaired = false;
    for (const [organizationId, state] of Object.entries(value)) {
        const clean = sanitizeAgentDockState(state);
        if (!clean) {
            repaired = true;
            continue;
        }
        repaired ||= clean.repaired;
        sanitized[organizationId] = clean.state;
    }
    return { value: sanitized, repaired };
}

/**
 * On a phone the dock's window covers the whole screen, so a page load never reopens it: the user
 * came to look at the page. Their tabs are kept, one tap away in the bar.
 */
function minimizeOnPhone(value: Record<string, AgentDockState>): Record<string, AgentDockState> {
    if (!window.matchMedia?.(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches) {
        return value;
    }
    return Object.fromEntries(
        Object.entries(value).map(([organizationId, state]) => [
            organizationId,
            { ...state, minimized: true },
        ])
    );
}

/** How much of a task stands in for the title of a tab persisted before titles existed. */
const UPGRADED_BACKGROUND_TAB_TITLE_LENGTH = 60;

/**
 * A persisted tab as this build renders it, and null when it cannot be read at all.
 *
 * Upgraded rather than dropped where the fields allow it, because a tab is somewhere the user put
 * themselves. A field renamed in future is added here rather than replacing the old one.
 */
export function normalizeAgentDockTab(value: unknown): AgentDockTab | null {
    if (!isRecord(value)) {
        return null;
    }

    // The first build to persist these named the work by its whole task; `title` came later.
    if (
        value.kind === 'organization_background' &&
        typeof value.title !== 'string' &&
        typeof value.task === 'string'
    ) {
        const { task, ...rest } = value;
        const upgraded = {
            ...rest,
            title: task.trim().slice(0, UPGRADED_BACKGROUND_TAB_TITLE_LENGTH),
        };
        return isAgentDockTab(upgraded) ? upgraded : null;
    }

    return isAgentDockTab(value) ? value : null;
}

/** Whether a value is a tab this build can render: every field the bar reads, checked. */
export function isAgentDockTab(value: unknown): value is AgentDockTab {
    if (!isRecord(value)) {
        return false;
    }

    switch (value.kind) {
        case 'organization':
            return typeof value.conversationId === 'string';
        case 'change_request':
            return (
                typeof value.spaceId === 'string' &&
                typeof value.changeRequestId === 'string' &&
                (value.conversationId === undefined || typeof value.conversationId === 'string')
            );
        case 'organization_background':
            return (
                typeof value.conversationId === 'string' &&
                typeof value.parentConversationId === 'string' &&
                typeof value.title === 'string'
            );
        default:
            return false;
    }
}

function sanitizeAgentDockState(
    value: unknown
): { state: AgentDockState; repaired: boolean } | null {
    if (!isRecord(value)) {
        return null;
    }

    const persisted = Array.isArray(value.tabs) ? value.tabs : [];
    const tabs = persisted.map(normalizeAgentDockTab).filter((tab) => tab !== null);
    // Dropped or upgraded: `normalizeAgentDockTab` returns the value itself when it is neither.
    let repaired =
        tabs.length !== persisted.length || tabs.some((tab, index) => tab !== persisted[index]);
    const draftConversationId =
        typeof value.draftConversationId === 'string' ? value.draftConversationId : null;

    // A pointer at a tab that was dropped would select nothing forever.
    const tabIds = new Set(tabs.map(getAgentDockTabId));
    const activeTabId =
        typeof value.activeTabId === 'string' &&
        (tabIds.has(value.activeTabId) ||
            (value.activeTabId === AGENT_DOCK_DRAFT_TAB_ID && draftConversationId !== null))
            ? value.activeTabId
            : null;

    const state: AgentDockState = {
        tabs,
        activeTabId,
        draftConversationId,
        minimized: value.minimized === true,
    };

    // Anything the read settled differently from what was stored.
    repaired ||=
        activeTabId !== (value.activeTabId ?? null) ||
        draftConversationId !== (value.draftConversationId ?? null) ||
        state.minimized !== (value.minimized ?? false);

    return { state, repaired };
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Stable identity of a tab, used as the React key, the active-tab pointer and the dedupe key when
 * the same conversation is opened twice.
 */
export function getAgentDockTabId(tab: AgentDockTab): string {
    switch (tab.kind) {
        case 'organization':
            return `organization:${tab.conversationId}`;
        case 'change_request':
            return `change_request:${tab.spaceId}:${tab.changeRequestId}:${tab.conversationId ?? 'default'}`;
        case 'organization_background':
            return `organization_background:${tab.conversationId}`;
    }
}

/**
 * The tab list with `tab` in it, unchanged when that conversation already has one.
 *
 * A conversation gets at most one tab, and every writer that opens one has to agree on that — the
 * bar, the draft being promoted, and the shortcuts from elsewhere in the product.
 */
function withTab(tabs: AgentDockTab[], tab: AgentDockTab): AgentDockTab[] {
    const tabId = getAgentDockTabId(tab);
    return tabs.some((open) => getAgentDockTabId(open) === tabId) ? tabs : [...tabs, tab];
}

function getOrganizationState(
    all: Record<string, AgentDockState>,
    organizationId: string
): AgentDockState {
    return all[organizationId] ?? EMPTY_STATE;
}

/**
 * Read and update the dock state of one organization.
 */
export function useAgentDockState(organizationId: string) {
    const [all, setAll] = useAtom(agentDockStateAtom);

    const state = React.useMemo(
        () => getOrganizationState(all, organizationId),
        [all, organizationId]
    );

    const update = React.useCallback(
        (updater: (previous: AgentDockState) => AgentDockState) => {
            setAll((previous) => ({
                ...previous,
                [organizationId]: updater(getOrganizationState(previous, organizationId)),
            }));
        },
        [organizationId, setAll]
    );

    return [state, update] as const;
}

/**
 * Whether this conversation is the draft: opened from the Agent button, and never written in.
 *
 * It holds nothing, by construction — a conversation earns a tab the moment it holds a message — so
 * anything that would otherwise wait for the agent to say a conversation is empty can read this and
 * not wait at all.
 */
export function useIsAgentDockDraftConversation(
    organizationId: string,
    conversationId: string
): boolean {
    const [state] = useAgentDockState(organizationId);
    return state.draftConversationId === conversationId;
}

/**
 * Open a conversation in the dock, without subscribing to its state.
 *
 * For the shortcuts scattered through the product — a reviewer row, a comment, a blankslate — which
 * only ever push a conversation to the front. {@link useAgentDock} reads the state as well, so a
 * caller using it re-renders whenever any conversation anywhere in the dock changes.
 */
export function useOpenAgentDockTab(organizationId: string): (tab: AgentDockTab) => void {
    const setAll = useSetAtom(agentDockStateAtom);

    return React.useCallback(
        (tab: AgentDockTab) => {
            const tabId = getAgentDockTabId(tab);
            setAll((all) => {
                const previous = getOrganizationState(all, organizationId);
                return {
                    ...all,
                    [organizationId]: {
                        ...previous,
                        tabs: withTab(previous.tabs, tab),
                        activeTabId: tabId,
                        minimized: false,
                    },
                };
            });
        },
        [organizationId, setAll]
    );
}

/**
 * Actions to drive the dock from anywhere in the application.
 */
export function useAgentDock(organizationId: string) {
    const [state, update] = useAgentDockState(organizationId);

    const openTab = React.useCallback(
        (tab: AgentDockTab) => {
            const tabId = getAgentDockTabId(tab);
            update((previous) => ({
                ...previous,
                tabs: withTab(previous.tabs, tab),
                activeTabId: tabId,
                minimized: false,
            }));
        },
        [update]
    );

    /**
     * Focus the draft conversation, adopting `conversationId` when there is no draft yet. Pressing
     * the Agent button while its conversation is already in front minimizes it, so the button
     * toggles like a tab.
     *
     * Takes the id rather than a factory on purpose: the caller has to mint and provision it
     * *before* calling, because this runs inside a state updater. React may run an updater during
     * a render pass it then discards, so a request started in here can be aborted before it
     * reaches the server — which is exactly how conversations ended up with no API token.
     */
    const toggleDraft = React.useCallback(
        (conversationId: string) => {
            update((previous) => {
                const isInFront =
                    previous.activeTabId === AGENT_DOCK_DRAFT_TAB_ID && !previous.minimized;
                return {
                    ...previous,
                    draftConversationId: previous.draftConversationId ?? conversationId,
                    activeTabId: AGENT_DOCK_DRAFT_TAB_ID,
                    minimized: isInFront,
                };
            });
        },
        [update]
    );

    /**
     * Give the draft conversation a tab, now that it has been written in.
     */
    const promoteDraft = React.useCallback(() => {
        update((previous) => {
            const conversationId = previous.draftConversationId;
            if (!conversationId) {
                return previous;
            }
            const tab: AgentDockTab = { kind: 'organization', conversationId };
            const tabId = getAgentDockTabId(tab);
            const wasInFront = previous.activeTabId === AGENT_DOCK_DRAFT_TAB_ID;
            return {
                ...previous,
                tabs: withTab(previous.tabs, tab),
                activeTabId: wasInFront ? tabId : previous.activeTabId,
                draftConversationId: null,
            };
        });
    }, [update]);

    const closeDraft = React.useCallback(() => {
        update((previous) => ({
            ...previous,
            draftConversationId: null,
            activeTabId:
                previous.activeTabId === AGENT_DOCK_DRAFT_TAB_ID ? null : previous.activeTabId,
        }));
    }, [update]);

    const closeTab = React.useCallback(
        (tabId: string) => {
            update((previous) => {
                const tabs = previous.tabs.filter((tab) => getAgentDockTabId(tab) !== tabId);
                if (tabs.length === previous.tabs.length) {
                    return previous;
                }
                return {
                    ...previous,
                    tabs,
                    // Closing the active tab just closes it — activating a neighbour would pop
                    // another conversation's window open uninvited.
                    activeTabId: previous.activeTabId === tabId ? null : previous.activeTabId,
                };
            });
        },
        [update]
    );

    /**
     * Bring a tab to the front, or minimize it when it is already there — the same toggle the
     * Agent button does for the draft, so every tab in the bar behaves the same way.
     */
    const selectTab = React.useCallback(
        (tabId: string) => {
            update((previous) => {
                const isInFront = previous.activeTabId === tabId && !previous.minimized;
                return { ...previous, activeTabId: tabId, minimized: isInFront };
            });
        },
        [update]
    );

    /**
     * Show one conversation, and drop whatever else was open.
     *
     * What the anchored panel does instead of {@link openTab}. It shows a single conversation and
     * has no row of open ones to add to, so opening another replaces the one before it — and going
     * back to the dock finds it holding exactly the conversation the panel was left on.
     */
    const openConversation = React.useCallback(
        (tab: AgentDockTab) => {
            update((previous) => ({
                ...previous,
                tabs: [tab],
                activeTabId: getAgentDockTabId(tab),
                minimized: false,
            }));
        },
        [update]
    );

    const setMinimized = React.useCallback(
        (minimized: boolean) => {
            update((previous) => ({ ...previous, minimized }));
        },
        [update]
    );

    /**
     * Close the tabs belonging to one change request.
     *
     * Named rather than inferred from what is current: the dock's state is shared between a user's
     * windows, so "everything that is not the change request in front of me" is a different set in
     * every window, and each would tear down the others' tabs. What a window can say for itself is
     * which change request *it* just left.
     *
     * Filtered inside the updater rather than from a list the caller holds, so the caller does not
     * have to depend on the tabs to do it.
     */
    const closeChangeRequestTabs = React.useCallback(
        (changeRequestId: string) => {
            update((previous) => {
                const tabs = previous.tabs.filter(
                    (tab) =>
                        tab.kind !== 'change_request' || tab.changeRequestId !== changeRequestId
                );
                if (tabs.length === previous.tabs.length) {
                    return previous;
                }

                const stillOpen = new Set(tabs.map((tab) => getAgentDockTabId(tab)));
                return {
                    ...previous,
                    tabs,
                    // As when a tab is closed by hand: nothing is activated in its place, rather
                    // than popping another conversation's window open uninvited.
                    activeTabId:
                        previous.activeTabId && stillOpen.has(previous.activeTabId)
                            ? previous.activeTabId
                            : null,
                };
            });
        },
        [update]
    );

    /**
     * Open the draft conversation, adopting `conversationId` when there is no draft yet. Unlike
     * {@link toggleDraft} this never minimizes: a caller starting a conversation from elsewhere in
     * the product wants it in front.
     */
    const startDraft = React.useCallback(
        (conversationId: string) => {
            update((previous) => ({
                ...previous,
                draftConversationId: previous.draftConversationId ?? conversationId,
                activeTabId: AGENT_DOCK_DRAFT_TAB_ID,
                minimized: false,
            }));
        },
        [update]
    );

    const activeTab = React.useMemo(
        () => state.tabs.find((tab) => getAgentDockTabId(tab) === state.activeTabId) ?? null,
        [state.tabs, state.activeTabId]
    );

    return {
        ...state,
        activeTab,
        isDraftActive: state.activeTabId === AGENT_DOCK_DRAFT_TAB_ID,
        openTab,
        closeTab,
        selectTab,
        openConversation,
        setMinimized,
        closeChangeRequestTabs,
        toggleDraft,
        startDraft,
        promoteDraft,
        closeDraft,
    };
}

/**
 * The page open on screen, published so the dock can offer it as context.
 *
 * The dock lives in the application frame while the content lives inside a screen, so the two cannot
 * reach each other through React context. The screen publishes here and the dock subscribes, which
 * keeps the page context working without the dock knowing anything about which screen is mounted.
 *
 * Published from the revision rather than from the editor: the title is in the revision as soon as
 * the user is on the page, and waiting for the document to load would leave the chip empty for as
 * long as the editor takes.
 */
export type AgentDockPageContext = {
    spaceId: string;
    /**
     * The change request the page is being edited in. Absent for a page edited live in its space,
     * where there is no change request in between.
     *
     * Both of its identifiers, because the two things this page is compared against address the same
     * change request differently: a tab carries the id, while the dock's own location carries
     * whatever the URL does — usually the number.
     */
    changeRequest?: {
        id: string;
        number: number;
    };
    page: RevisionPage;
};

const agentDockPageAtom = atom<AgentDockPageContext | null>(null);

/**
 * Publish the on-screen page to the dock for as long as the calling component is mounted. Pass null
 * where there is nothing to offer, e.g. a page the user cannot edit.
 */
export function usePublishAgentDockPage(value: AgentDockPageContext | null) {
    const setPage = useSetAtom(agentDockPageAtom);
    const { spaceId, page } = value ?? {};
    // Taken apart so the effect runs on the change request's identity rather than on the identity of
    // the object it arrived in, which a caller building it inline mints on every render.
    const changeRequestId = value?.changeRequest?.id;
    const changeRequestNumber = value?.changeRequest?.number;

    React.useEffect(() => {
        if (!spaceId || !page) {
            return;
        }

        setPage({
            spaceId,
            ...(changeRequestId && changeRequestNumber !== undefined
                ? { changeRequest: { id: changeRequestId, number: changeRequestNumber } }
                : {}),
            page,
        });
        // Only ever clears its own publication: an overlay screen mounting over this one publishes
        // its own page, and this one's unmount must not take that with it. The change request is
        // part of the identity, not just the space and the page: a change-request screen can overlay
        // the same page of the same space that is being edited live, and without this its unmount
        // would clear the publication underneath it — which nothing re-publishes.
        return () =>
            setPage((current) =>
                current?.page.id === page.id &&
                current?.spaceId === spaceId &&
                current?.changeRequest?.id === changeRequestId
                    ? null
                    : current
            );
    }, [setPage, spaceId, changeRequestId, changeRequestNumber, page]);
}

export function useAgentDockPage() {
    return useAtomValue(agentDockPageAtom);
}

/**
 * The editor mounted for that page, published so the dock can watch the text selected in it.
 *
 * Its ref rather than a snapshot of the selection, so the polling that watches the selection only
 * runs while a dock window is actually open. Separate from the page above because it arrives later:
 * the editor exists once the document has loaded, the page as soon as the revision has.
 */
export type AgentDockEditorContext = {
    pageId: string;
    editor: EditorContainer | null;
};

const agentDockEditorAtom = atom<AgentDockEditorContext | null>(null);

/**
 * Publish the on-screen editor to the dock for as long as the calling component is mounted.
 *
 * Takes the ref and reads the instance off it here so that no consumer has to touch a ref while
 * rendering — the editor is published as a value, which is what the dock can safely watch.
 */
export function usePublishAgentDockEditor(
    value: { pageId: string; editorRef: React.RefObject<EditorContainer | null> } | null
) {
    const setEditor = useSetAtom(agentDockEditorAtom);
    const { pageId, editorRef } = value ?? {};

    // Deliberately without a dependency list: the editor lands on the ref during a commit of its
    // own, with nothing to depend on. The write bails out when nothing changed, so watching a page
    // being typed into does not re-render the dock on every keystroke.
    React.useEffect(() => {
        if (!pageId || !editorRef) {
            return;
        }

        const editor = editorRef.current ?? null;
        setEditor((current) =>
            current?.pageId === pageId && current.editor === editor ? current : { pageId, editor }
        );
    });

    // Owns the publication's lifetime, which the sync above cannot: a cleanup running after every
    // render would clear and re-publish continuously.
    React.useEffect(() => {
        if (!pageId) {
            return;
        }

        return () => setEditor((current) => (current?.pageId === pageId ? null : current));
    }, [setEditor, pageId]);
}

export function useAgentDockEditor() {
    return useAtomValue(agentDockEditorAtom);
}

/**
 * The analytics report on screen, published so the dock can welcome the user with it and show it
 * as the context the conversation starts with. Its filters reach the agent through the URL.
 */
export type AgentDockAnalyticsContext = {
    siteId: string;
    /** The report's name, e.g. "Traffic". */
    label: string;
    /** What it is narrowed to, e.g. "Last 7 days". */
    detail: string;
    icon: IconDefinition;
};

const agentDockAnalyticsAtom = atom<AgentDockAnalyticsContext | null>(null);

/**
 * Publish the on-screen analytics report to the dock for as long as the calling component is
 * mounted.
 */
export function usePublishAgentDockAnalytics(value: AgentDockAnalyticsContext | null) {
    const setAnalytics = useSetAtom(agentDockAnalyticsAtom);
    const { siteId, label, detail, icon } = value ?? {};

    React.useEffect(() => {
        if (!siteId || !label || !detail || !icon) {
            return;
        }

        const published = { siteId, label, detail, icon };
        setAnalytics(published);
        // Only ever clears its own publication, like the page above.
        return () => setAnalytics((current) => (current === published ? null : current));
    }, [setAnalytics, siteId, label, detail, icon]);
}

export function useAgentDockAnalytics() {
    return useAtomValue(agentDockAnalyticsAtom);
}
